export type LinkBuilderCampaign = {
  network: string
  publisherId?: string
  url: string
}

export type TrackingLinkResult =
  | { link: string; error: null }
  | { link: ''; error: string }

const HTTP_PROTOCOL_PATTERN = /^https?:\/\//i

export function getCampaignDomains(shopUrl: string) {
  const domains: string[] = []
  const seen = new Set<string>()

  String(shopUrl || '').split('||').forEach((rawValue) => {
    let candidate = rawValue.trim()
    if (!candidate) return

    candidate = candidate
      .replace(HTTP_PROTOCOL_PATTERN, '')
      .replace(/^\/\//, '')

    const isWildcard = candidate.startsWith('*.')
    if (isWildcard) candidate = candidate.slice(2)

    try {
      const hostname = new URL(`https://${candidate}`)
        .hostname
        .toLowerCase()
        .replace(/^www\./, '')
        .replace(/\.$/, '')

      if (!hostname) return

      const domain = isWildcard ? `*.${hostname}` : hostname
      if (seen.has(domain)) return

      seen.add(domain)
      domains.push(domain)
    } catch {
      // Invalid values should not be presented as usable campaign domains.
    }
  })

  return domains
}

export function campaignRequiresDestination(campaign: LinkBuilderCampaign | null | undefined) {
  if (!campaign) return false
  return campaign.url.startsWith('NP-') || campaign.url.includes('__dl__')
}

export function getDestinationError(value: string, required: boolean) {
  const trimmed = String(value || '').trim()

  if (!trimmed) {
    return required ? 'Enter the destination URL for this campaign.' : ''
  }

  const withProtocol = HTTP_PROTOCOL_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`

  try {
    const parsed = new URL(withProtocol)
    if (!parsed.hostname || !['http:', 'https:'].includes(parsed.protocol)) {
      return 'Enter a valid web address, such as https://example.com/page.'
    }
  } catch {
    return 'Enter a valid web address, such as https://example.com/page.'
  }

  return ''
}

function buildNordparLink(
  rawDeepLink: string,
  agreementId: string,
  publisherId: string,
  clickReference: string,
) {
  const trimmed = String(rawDeepLink || '').trim()
  const normalizedAgreementId = String(agreementId || '').trim()
  const normalizedPublisherId = String(publisherId || '').trim()

  const refValue = normalizedPublisherId && !normalizedAgreementId.endsWith(`-${normalizedPublisherId}`)
    ? `${normalizedAgreementId}-${normalizedPublisherId}`
    : normalizedAgreementId

  const withProtocol = HTTP_PROTOCOL_PATTERN.test(trimmed)
    ? trimmed
    : `https://${trimmed.replace(/^\/+/, '')}`

  const parsed = new URL(withProtocol)
  parsed.searchParams.set('np-ref', refValue)

  const normalizedClickReference = String(clickReference || '').trim()
  if (normalizedClickReference) {
    parsed.searchParams.set('np-click-ref', normalizedClickReference)
  }

  return parsed.toString()
}

export function buildTrackingLink(
  campaign: LinkBuilderCampaign,
  deepLink: string,
  clickReference: string,
): TrackingLinkResult {
  const template = String(campaign.url || '')
  if (!template) {
    return { link: '', error: 'This campaign does not have a link template. Please contact support.' }
  }

  const requiresDestination = campaignRequiresDestination(campaign)
  const destinationError = getDestinationError(deepLink, requiresDestination)
  if (requiresDestination && destinationError) {
    return { link: '', error: destinationError }
  }

  if (template.startsWith('NP-')) {
    const agreementId = template.slice(3).trim()
    if (!agreementId) {
      return { link: '', error: 'This campaign is missing its Nordpar agreement ID. Please contact support.' }
    }

    try {
      return {
        link: buildNordparLink(deepLink, agreementId, campaign.publisherId || '', clickReference),
        error: null,
      }
    } catch {
      return { link: '', error: 'The destination URL could not be used to build a tracking link.' }
    }
  }

  const normalizedDeepLink = String(deepLink || '').trim()
  let finalDeepLink = normalizedDeepLink

  if (campaign.network === 'Daisycon' && normalizedDeepLink) {
    try {
      const parsed = new URL(normalizedDeepLink)
      finalDeepLink = parsed.pathname.startsWith('/') ? parsed.pathname.slice(1) : parsed.pathname
    } catch {
      // Preserve the existing Daisycon behaviour for destination values without a protocol.
      finalDeepLink = normalizedDeepLink
    }
  }

  return {
    link: template
      .replace('__ref__', clickReference)
      .replace('__dl__', encodeURIComponent(finalDeepLink)),
    error: null,
  }
}
